<template>
  <div>
    <div>
      <h1 class="mdn-Heading mdn-Heading--sm">Provedores analíticos</h1>

      <div class="btn-container">
        <ModosWebBotaoExportarComponent v-if="linhas.length" :campos="filtro"
          @exportar="handleExportarAnaliticosProvedor" />
        <button v-if="linhas.length" type="button"
          class="btn-toggle mb-3 v-btn v-btn--has-bg theme--light v-size--default primary button-orientation"
          @click="alternarFiltro">
          {{ mostrarFiltro ? 'Ocultar filtros' : 'Mostrar filtros' }}
        </button>
        <ModosWebFiltroInlineCollapseComponent ref="filtroInline" :campos="camposFiltrosComputed" :key="filtroKey"
          :inicialOpen="mostrarFiltro" :esconderBotao="true" @open-change="mostrarFiltro = $event"
          @modosWebFormFilterDadosClick="aplicarFiltro" @modosWebFormFilterDadosChange="atualizarFiltroEmEdicao"
          @modosWebFormFilterLimpar="resetarFiltro" />
      </div>

      <template v-if="linhas.length">
        <SGPPaginacaoComponent :paginacao="paginacao" :key="paginacao?.current_page"
          @sgpAtualizarPaginacao="realizarPaginacao">
          <SGPTabelaComponent :colunas="colunas" :dados="linhas" :usuarioPodeEditar="false" :usuarioPodeExcluir="false"
            :botaoInline="false" :somenteLeitura="true" @sgpTabelaModalCadastrarEmit="abrirDetalheProvedor">
             
            <template v-slot:item.percentual_dentro_sla="{ item }">
              {{ formatarPercentual(item.percentual_dentro_sla) }}
            </template>
          </SGPTabelaComponent>
          </SGPPaginacaoComponent>
      </template>

      <SGPAlertaComponent v-else />
      <SGPLoadingComponent v-if="loadingAnaliticosProvedor" />
    </div>
  </div>
</template>

<style scoped>
.btn-container {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  justify-content: flex-end !important;
  flex-wrap: wrap;
  align-items: flex-start;
}

.btn-container > :last-child {
  flex: 0 0 100%;
}
</style>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import ModosWebFiltroInlineCollapseComponent from '@/common/modosweb-components/ModosWebFiltroInlineCollapseComponent.vue'
import ModosWebBotaoExportarComponent from '@/common/modosweb-components/ModosWebBotaoExportarComponent.vue'
import { formatarPercentual } from '@/common/boot/Utils'

const TIPO_DATA_PADRAO = 'data_referencia'

const TIPOS_QTD_OPCOES = [
  {
    value: 'qtd_solicitacoes_interrupcao',
    text: 'Qtd. solicitações de interrupção'
  },
  {
    value: 'qtd_interrupcoes_aprovadas',
    text: 'Qtd. interrupções aprovadas'
  },
  {
    value: 'qtd_interrupcoes_negadas',
    text: 'Qtd. interrupções negadas'
  },
  {
    value: 'qtd_procedente',
    text: 'Qtd. procedentes'
  },
  {
    value: 'qtd_improcedente',
    text: 'Qtd. improcedentes'
  },
  {
    value: 'qtd_dentro_sla',
    text: 'Qtd. dentro SLA'
  },
  {
    value: 'qtd_fora_sla',
    text: 'Qtd. Fora SLA'
  },
]

const DATA_REFERENCIA_OPCOES = [
  {
    value: 'data_referencia',
    text: 'Data livre'
  },
  {
    value: 'data_mensal',
    text: 'Data mensal'
  }
]

const TIPOS_QTD_VALUES = TIPOS_QTD_OPCOES.map((opcao) => opcao.value)
const DATA_REFERENCIA_VALUES = DATA_REFERENCIA_OPCOES.map((opcao) => opcao.value)

export default {
  name: 'ProvedoresAnaliticosPage',

  components: {
    ModosWebFiltroInlineCollapseComponent,
    ModosWebBotaoExportarComponent
  },

  data() {
    return {
      mostrarFiltro: false,
      filtro: {},
      filtroKey: 1
    }
  },

  created() {
    this.buscarAnaliticosProvedor()
    this.listarCtrlEstados()
  },

  computed: {
    ...mapGetters('ctrlAnaliticosProvedor', [
      'listaDeAnaliticosProvedor',
      'loadingAnaliticosProvedor',
      'paginacao'
    ]),

    ...mapGetters('ctrlEstadosWf', ['listaDeCtrlEstados']),

    camposFiltrosComputed() {
      const tipoDataSelecionado = this.obterTipoDataSelecionado()

      return [
        this.criarCampoProvedor(),
        this.criarCampoTipoData(tipoDataSelecionado),
        this.criarCampoValorData(tipoDataSelecionado),
        this.criarCampoTipoQuantidade(),
        this.criarCampoValorQuantidade(),
        this.criarCampoUF(),
        this.criarCampoCM(),
      ]
    },

    linhas() {
      return this.listaDeAnaliticosProvedor.map((row) => ({
        ...row,
        modalGenerica: `/workflow/ficha-tecnica-tarefa/${row.provedor_id}`,
        data_referencia_fmt: this.formatarDataTabela(row.referencia_exibicao)
      }))
    },

    colunas() {
      return [
        { key: "modalGenerica", label: "Detalhes" },
        { key: 'provedor.nome', label: 'Provedor', sortable: false },
        { key: 'data_referencia_fmt', label: 'Data referência', sortable: false },
        { key: 'provedor.responsavel', label: 'Responsável', sortable: false },
        {
          key: 'qtd_solicitacoes_interrupcao',
          label: 'Solicitações interrupção',
          sortable: false
        },
        {
          key: 'qtd_interrupcoes_aprovadas',
          label: 'Interrupções aprovadas',
          sortable: false
        },
        {
          key: 'qtd_interrupcoes_negadas',
          label: 'Interrupções negadas',
          sortable: false
        },
        { key: 'qtd_procedente', label: 'Procedente', sortable: false },
        { key: 'qtd_improcedente', label: 'Improcedente', sortable: false },
        { key: 'qtd_dentro_sla', label: 'Dentro SLA', sortable: false },
        { key: 'percentual_dentro_sla', label: '% Dentro do SLA', sortable: false },
        { key: 'qtd_fora_sla', label: 'Fora SLA', sortable: false },
        { key: 'qtd_chamados', label: 'Quantidade de Chamados', sortable: false },
        
      ]
    }
  },

  methods: {
    ...mapActions('ctrlAnaliticosProvedor', [
      'listarAnaliticosProvedor',
      'exportarAnaliticosProvedor'
    ]),

    ...mapActions('ctrlEstadosWf', ['listarCtrlEstados']),

    formatarPercentual,

    abrirDetalheProvedor(provedor) {
      const route = this.$router.resolve({
        name: 'analiticos/detalhe-provedor',
        query: {
          provedorId: provedor.provedor_id,
          nomeProvedor: provedor.provedor.nome,
          ...this.filtro
        }
      })

      window.open(route.href, '_blank')
    },

    criarCampoProvedor() {
      return {
        name: 'provedor',
        type: 'text',
        label: 'Provedor',
        value: this.filtro?.provedor || ''
      }
    },

    criarCampoTipoData(tipoDataSelecionado) {
      return {
        name: 'tipo_data_referencia',
        type: 'select',
        label: 'Tipo de data',
        options: DATA_REFERENCIA_OPCOES,
        value: tipoDataSelecionado
      }
    },

    criarCampoValorData(tipoDataSelecionado) {
      const ehFiltroMensal = tipoDataSelecionado === 'data_mensal'

      return {
        name: 'valor_data_referencia',
        type: ehFiltroMensal ? 'month' : 'date',
        label: ehFiltroMensal ? 'Data mensal' : 'Data livre',
        value: this.filtro?.valor_data_referencia || null
      }
    },

    criarCampoTipoQuantidade() {
      return {
        name: 'tipo_qtd',
        type: 'select',
        label: 'Filtrar por quantidade (campo)',
        options: TIPOS_QTD_OPCOES,
        value: this.filtro?.tipo_qtd || ''
      }
    },

    criarCampoValorQuantidade() {
      return {
        name: 'valor_qtd',
        type: 'text',
        label: 'Valor da quantidade',
        value: this.filtro?.valor_qtd || ''
      }
    },

    criarCampoUF() {
      return {
        name: 'uf',
        type: 'select',
        label: 'UF',
        options: this.listaDeCtrlEstados.map(item => {
            return { value: item.uf, text: item.uf }
        }),
        value: this.filtro?.uf || ''
      }
    },

    criarCampoCM() {
      return {
        name: 'cm',
        type: 'text',
        label: 'CM',
        value: this.filtro?.cm || ''
      }
    },

    obterTipoDataSelecionado() {
      return this.filtro?.tipo_data_referencia || TIPO_DATA_PADRAO
    },

    construirFiltroPayload(filtro = {}) {
      return {
        ...this.construirFiltroData(filtro),
        ...this.construirFiltroProvedor(filtro),
        ...this.construirFiltroQuantidade(filtro),
        ...this.construirFiltroTexto(filtro, 'uf'),
        ...this.construirFiltroTexto(filtro, 'cm'),
      }
    },

    construirFiltroData(filtro = {}) {
      const tipoData = filtro.tipo_data_referencia || TIPO_DATA_PADRAO
      const valorData = filtro.valor_data_referencia

      if (!this.filtroDataValido(tipoData, valorData)) {
        return {}
      }

      if (tipoData === 'data_mensal') {
        return {
          data_mensal: valorData
        }
      }

      return {
        data_referencia: this.formatarDataFiltro(valorData)
      }
    },

    construirFiltroProvedor(filtro = {}) {
      const provedor = this.normalizarTexto(filtro.provedor)

      if (!provedor) {
        return {}
      }

      return {
        provedor
      }
    },

    construirFiltroTexto(filtro = {}, campo) {
      const valor = this.normalizarTexto(filtro[campo])

      if (!valor) {
        return {}
      }

      return {
        [campo]: valor
      }
    },

    construirFiltroQuantidade(filtro = {}) {
      const tipoQuantidade = filtro.tipo_qtd
      const valorQuantidade = this.normalizarValorQuantidade(filtro.valor_qtd)

      if (!this.filtroQuantidadeValido(tipoQuantidade, valorQuantidade)) {
        return {}
      }

      return {
        tipo_qtd: tipoQuantidade,
        valor_qtd: valorQuantidade
      }
    },

    filtroDataValido(tipoData, valorData) {
      return Boolean(
        tipoData &&
        valorData &&
        DATA_REFERENCIA_VALUES.includes(tipoData)
      )
    },

    filtroQuantidadeValido(tipoQuantidade, valorQuantidade) {
      return Boolean(
        tipoQuantidade &&
        valorQuantidade !== null &&
        TIPOS_QTD_VALUES.includes(tipoQuantidade)
      )
    },

    normalizarTexto(valor) {
      if (valor === null || valor === undefined) {
        return ''
      }

      return String(valor).trim()
    },

    normalizarValorQuantidade(valor) {
      const valorNormalizado = this.normalizarTexto(valor)

      if (!valorNormalizado) {
        return null
      }

      const numero = parseInt(valorNormalizado, 10)

      return Number.isNaN(numero) ? null : numero
    },

    formatarDataFiltro(valorData) {
      const data = moment(valorData)

      return data.isValid()
        ? data.format('YYYY-MM-DD')
        : valorData
    },

    valorDataEhMensal(valorData) {
      const valorTexto = String(valorData).trim()

      return /^\d{4}-\d{2}$/.test(valorTexto) || /^\d{2}\/\d{4}$/.test(valorTexto)
    },

    formatarDataMensal(valorData) {
      const valorTexto = String(valorData).trim()

      if (/^\d{2}\/\d{4}$/.test(valorTexto)) {
        return valorTexto
      }

      return moment(valorTexto, 'YYYY-MM', true).format('MM/YYYY')
    },

    formatarDataTabela(valorData) {
      if (!valorData) {
        return '-'
      }

      const tipoData = this.obterTipoDataSelecionado()
      const formatoMensal = tipoData === 'data_mensal' || this.valorDataEhMensal(valorData)

      if (formatoMensal) {
        return this.formatarDataMensal(valorData)
      }

      return moment.utc(valorData).format('DD/MM/YYYY')
    },

    montarParametros(extra = {}) {
      return {
        per_page: this.obterPerPagePadrao(),
        page: this.obterPaginaAtualPadrao(),
        current_page: this.obterPaginaAtualPadrao(),
        ...this.construirFiltroPayload(this.filtro),
        ...extra
      }
    },

    obterPerPagePadrao() {
      return this.paginacao?.per_page || 20
    },

    obterPaginaAtualPadrao() {
      return this.paginacao?.current_page || 1
    },

    buscarAnaliticosProvedor(extra = {}) {
      return this.listarAnaliticosProvedor(this.montarParametros(extra))
    },

    buscarPrimeiraPagina() {
      return this.buscarAnaliticosProvedor({
        page: 1,
        current_page: 1
      })
    },

    alternarFiltro() {
      this.mostrarFiltro = !this.mostrarFiltro

      if (this.$refs.filtroInline?.toggleOpen) {
        this.$refs.filtroInline.toggleOpen()
      }
    },

    resetarFiltro() {
      this.filtro = {}
      this.filtroKey += 1

      this.buscarPrimeiraPagina()
    },

    async aplicarFiltro(dados) {
      this.filtro = dados || {}

      await this.buscarPrimeiraPagina()
    },

    atualizarFiltroEmEdicao(dados) {
      const tipoAnterior = this.obterTipoDataSelecionado()
      const tipoNovo = dados?.tipo_data_referencia || TIPO_DATA_PADRAO

      this.filtro = dados || {}

      if (tipoAnterior !== tipoNovo) {
        this.limparValorDataReferencia()
      }
    },

    limparValorDataReferencia() {
      this.filtro.valor_data_referencia = null
      this.filtroKey += 1
    },

    realizarPaginacao(paginacao) {
      const paginaAtual = paginacao.current_page || 1
      const porPagina = paginacao.per_page || this.obterPerPagePadrao()

      this.buscarAnaliticosProvedor({
        page: paginaAtual,
        current_page: paginaAtual,
        per_page: porPagina
      })
    },

    async handleExportarAnaliticosProvedor(dados) {
      const payload = {
        ...this.construirFiltroPayload(dados?.filtro || this.filtro),
        formato: dados?.formato || 'csv'
      }

      await this.exportarAnaliticosProvedor(payload)
    }
  }
}
</script>